import { Select, Spin, message, Modal } from 'antd'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector, shallowEqual } from 'dva'
import { CardTable } from './components'
import { ProductErrorModal } from 'components'
import { ProductErrorModalProps } from 'components/ProductErrorModal'
import { useRequest } from '@sisyphe/hooks'
import * as api from 'services/store'
import './index.less'
import { useMemo, useState } from 'react'
import { toFixed } from '@sisyphe/utils'

type Props = {}

const selector = ({ cart, login }) => ({
  amount: cart.amount,
  userInfo: login.userInfo || {},
})

const Cart = (props: Props) => {
  const dispatch = useDispatch()
  const { amount, userInfo } = useSelector(selector, shallowEqual)
  const history = useHistory()
  const [address, setAddress] = useState<number>()
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorType, setErrorType] =
    useState<ProductErrorModalProps['type']>('overInventory')
  const [errorList, setErrorList] = useState<
    Array<SwaggerDTO.校验无法通过的商品信息>
  >([])

  // 购物车管理-数量编辑
  const { run: shopCartEditUsingPOST } = useRequest(
    async data => {
      await api.shopCartEditUsingPOST(data)
    },
    { manual: true }
  )

  // 购物车管理-购物车列表
  const {
    run: shopCartListUsingPOST,
    loading,
    data: ListData,
  } = useRequest(async () => {
    let { data } = await api.shopCartListUsingPOST()

    // 设置默认值
    data?.addressInfoDTOS?.length &&
      setAddress(data.addressInfoDTOS?.find(c => c.defaultAddressTag)?.id)

    if (!data?.cartInfoDTOS) return { data, skuStocks: {} }

    // 处理列表数据
    data?.cartInfoDTOS?.forEach(item => {
      item.stockStatus = !!item?.soldOut
      item.shelfStatus =
        item.skuPublishStatus !== 2 ||
        item.onSale !== 1 ||
        !!item.advanceSale ||
        item.localCategoryDownFlag
    })
    // 查询库存
    const { data: stocks } = await api.judgeCacheUsingPOST({
      skuCodeList: data?.cartInfoDTOS?.map(c => c.skuCode),
    })
    // 库存信息
    const skuStocks = {}
    stocks?.forEach(item => {
      skuStocks[item.skuCode] = item
      // 找到对应购物车的商品，判断数量是否大于了最大库存，大于最大库存了需要手动同步到最大库存
      let skuData = data?.cartInfoDTOS?.find(c => c.skuCode === item.skuCode)
      if ((skuData?.num || 0) > item.usableStock) {
        shopCartEditUsingPOST({ id: skuData?.id, num: item.usableStock })
      }
    })

    return { data, skuStocks }
  })

  const { data, skuStocks } = ListData || {}

  // 购物车管理-结算接口
  const { run: checkOrderUsingPOST, loading: settlementLoading } = useRequest(
    async params => {
      const { data } = await api.checkOrderUsingPOST(params)
      // 错误信息
      if (data?.unPassData?.length) {
        // 库存不足
        const overInventoryList = data.unPassData.filter(c => c.code === 1000)
        if (overInventoryList.length) {
          setErrorType('overInventory')
          setErrorList([...overInventoryList])
        }
        // 下架
        const offList = data.unPassData.filter(c =>
          [2000, 3000].includes(c.code)
        )
        if (offList.length) {
          setErrorType('off')
          setErrorList(offList)
        }
        setErrorOpen(true)
        return false
      }

      await dispatch({
        type: 'order/setOrderInfo',
        payload: params,
      })
      history.push('/settlement')
      return data
    },
    { manual: true }
  )

  // 权限
  const roleCodes = useMemo(() => {
    let codes = userInfo?.roles?.map(c => c.name)
    return codes || []
  }, [userInfo])

  // 结算
  const onSettlement = async values => {
    if (!values.spuInfos.length) {
      return message.warning('请选择商品')
    }

    const addressObj: SwaggerDTO.地址信息 | undefined =
      data?.addressInfoDTOS?.find(item => item?.id === address)

    const params = {
      ...values,
      addressInfo: {
        ...addressObj,
        consigneeAddress: addressObj?.address,
        consigneeMan: addressObj?.addressee,
        consigneeMobile: addressObj?.linkMobile,
        detail: addressObj?.address,
      },
    }
    await checkOrderUsingPOST(params)
  }

  const productErrorOk = () => {
    shopCartListUsingPOST()
    setErrorOpen(false)
  }

  return (
    <div className="cart-box">
      <div className="cart-header">
        <p className="cart-header-left">
          <span>全部商品</span>
          <span className="header-left-amount">{amount}</span>
        </p>
        <div className="cart-header-right">
          <div>
            {roleCodes.includes('role:common') && (
              <>
                <p className="header-right-price">
                  最低下单金额：¥{toFixed(data?.minPlaceOrderAmount)}
                </p>
                <p className="header-right-price">
                  当前可用额度：¥{toFixed(data?.totalAmount)}
                </p>
              </>
            )}
          </div>
          {!!data?.addressInfoDTOS?.length && (
            <Select
              value={address}
              className="header-right-select"
              placeholder="请选择地址"
              options={data?.addressInfoDTOS?.map(item => ({
                label: `${item.provinceName}${item.cityName}${item.areaName}${item.address}`,
                value: item.id,
                ...item,
              }))}
              onChange={setAddress}
            />
          )}
        </div>
      </div>
      <Spin spinning={loading || settlementLoading}>
        <CardTable
          data={data?.cartInfoDTOS}
          skuStocks={skuStocks}
          deleteCallback={shopCartListUsingPOST}
          onSettlement={onSettlement}
        />
      </Spin>
      <Modal
        open={errorOpen}
        closable={false}
        width={350}
        onCancel={() => setErrorOpen(false)}
        maskClosable={false}
        footer={false}
        keyboard={false}
        destroyOnClose
      >
        <ProductErrorModal
          onOk={productErrorOk}
          type={errorType}
          data={errorList}
        />
      </Modal>
    </div>
  )
}

export default Cart
